import type { Pagination } from '@/lib/api/types.js';
import { cn } from '@/lib/utils.js';

interface CatalogPaginationProps {
  pagination: Pagination;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const;

export function CatalogPagination({
  pagination,
  onPageChange,
  onPageSizeChange,
}: CatalogPaginationProps) {
  const { page_number, page_size, total_pages, total_items } = pagination;

  const hasPrev = page_number > 1;
  const hasNext = page_number < total_pages;

  function pageNumbers(): number[] {
    const pages: number[] = [];
    const start = Math.max(1, page_number - 2);
    const end = Math.min(total_pages, page_number + 2);
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  }

  if (total_pages <= 1 && total_items <= page_size) return null;

  return (
    <div className="flex flex-col items-center gap-3 py-4 sm:flex-row sm:justify-between">
      {/* Total info */}
      <p className="text-sm text-[var(--color-qup-text-muted-dark)]">
        {total_items.toLocaleString('es')} informes · Página {page_number} de {total_pages}
      </p>

      <div className="flex items-center gap-3">
        {/* Page size selector */}
        <div className="flex items-center gap-2">
          <label
            htmlFor="page-size-select"
            className="text-xs text-[var(--color-qup-text-muted-dark)]"
          >
            Por página:
          </label>
          <select
            id="page-size-select"
            value={page_size}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className={cn(
              'rounded border border-[var(--color-qup-border-dark)] bg-[var(--color-qup-bg-card-dark)]',
              'px-2 py-1 text-xs text-[var(--color-qup-text-dark)]',
              'focus:outline-none focus:ring-2 focus:ring-[var(--color-qup-primary)]',
            )}
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>

        {/* Page buttons */}
        <nav aria-label="Paginación" className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onPageChange(page_number - 1)}
            disabled={!hasPrev}
            aria-label="Página anterior"
            className={cn(
              'flex h-8 w-8 items-center justify-center rounded border text-sm transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-qup-primary)]',
              hasPrev
                ? 'border-[var(--color-qup-border-dark)] text-[var(--color-qup-text-dark)] hover:border-[var(--color-qup-primary)] hover:text-[var(--color-qup-primary)]'
                : 'cursor-not-allowed border-[var(--color-qup-border-dark)]/40 text-[var(--color-qup-text-muted-dark)]/40',
            )}
          >
            ‹
          </button>

          {pageNumbers().map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => onPageChange(n)}
              aria-current={n === page_number ? 'page' : undefined}
              aria-label={`Página ${n}`}
              className={cn(
                'flex h-8 w-8 items-center justify-center rounded border text-sm font-medium transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-qup-primary)]',
                n === page_number
                  ? 'border-[var(--color-qup-primary)] bg-[var(--color-qup-primary)] text-white'
                  : 'border-[var(--color-qup-border-dark)] text-[var(--color-qup-text-dark)] hover:border-[var(--color-qup-primary)] hover:text-[var(--color-qup-primary)]',
              )}
            >
              {n}
            </button>
          ))}

          <button
            type="button"
            onClick={() => onPageChange(page_number + 1)}
            disabled={!hasNext}
            aria-label="Página siguiente"
            className={cn(
              'flex h-8 w-8 items-center justify-center rounded border text-sm transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-qup-primary)]',
              hasNext
                ? 'border-[var(--color-qup-border-dark)] text-[var(--color-qup-text-dark)] hover:border-[var(--color-qup-primary)] hover:text-[var(--color-qup-primary)]'
                : 'cursor-not-allowed border-[var(--color-qup-border-dark)]/40 text-[var(--color-qup-text-muted-dark)]/40',
            )}
          >
            ›
          </button>
        </nav>
      </div>
    </div>
  );
}
